// useChat — owns chat history, in-flight request state, the rolling
// current-image lineage, and the AbortController used to cancel.
//
// v4 contract (preserved):
//   - chatHistory / currentImageUrl / currentThumbnailUrl /
//     currentParameters / currentImageId / parentId / currentSeed
//   - status / sending / errorMessage
//   - variations / pickingVariation
//   - send / applyParameters / generateFresh /
//     pickVariation / dismissVariations
//   - updateParameter / updateParameterGroup
//   - saveToCatalogue / savingToCatalogue / saveErrorMessage
//   - reset / clearError
//
// v5 additions (new):
//   - cancel() — aborts the active request without changing chat state
//     beyond reverting status to 'idle' and clearing errorMessage.
//   - retry() — re-sends the most recent user-visible chat message.
//   - setAsStartingPoint(...) — applies a saved eye's parameters/seed
//     to the Studio without going through chat (used by CatalogueCard
//     "Set as starting point" and by the LineageStrip).
//   - The ChatTurn type now optionally carries a `diff` array used by the
//     ChatPanel's inline diff bubble.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  isVariationResponse,
  type ChatMessage,
  type ChatResponse,
  type ChatResponseItem,
  type ChatVariationResponse,
  type CatalogueItem,
  type SaveCatalogueRequest,
} from '../api/client';
import { friendlyError } from '../lib/errors';
import { DEFAULT_PARAMETERS, type Parameters } from '../types/parameters';
import type { Annotation } from '../types/annotations';
import { computeParameterDiff } from '../lib/diff';
import {
  loadPersistedEye,
  savePersistedEye,
  clearPersistedEye,
} from '../lib/persistedEye';
import {
  loadPersistedChat,
  savePersistedChat,
  clearPersistedChat,
} from '../lib/persistedChat';
import {
  loadAnnotations,
  saveAnnotations,
  clearAllAnnotations,
} from '../lib/persistedAnnotations';

/**
 * One turn in the visible chat panel. The optional `diff` field is
 * populated for assistant turns so the diff bubble can render without
 * recomputing it later.
 *
 * v7 additions:
 *   - `description` — plain-language description of the eye that this
 *      assistant turn refers to. Fetched from `/api/description` after the
 *      image lands so the chat panel can show a one-line subtitle for
 *      aphantasia-friendly browsing.
 *   - `selectedVariationId` — for assistant turns that followed a
 *      variations grid pick, the id of the chosen tile so the chat
 *      history can highlight it.
 */
export interface ChatTurn {
  /** Stable id used for React keys + diff collapse state. */
  id: number;
  role: 'user' | 'assistant';
  content: string;
  diff?: { parameter: string; before: unknown; after: unknown }[];
  /** v7 — plain-language description (only on assistant turns). */
  description?: string;
  /** v7 — for variation-pick turns, the items + which one was chosen. */
  variation?: {
    items: { id: string; image_url: string; thumbnail_url?: string; seed: number }[];
    selected_id: string;
  };
}

export type ChatStatus = 'idle' | 'sending' | 'error';

export interface UseChatResult {
  /** Rolling chat history shown in the panel. */
  chatHistory: ChatTurn[];
  /** Backend-relative URL of the most recent generated image, or null. */
  currentImageUrl: string | null;
  /** Backend-relative thumbnail URL of the most recent generated image. */
  currentThumbnailUrl: string | null;
  /** Current Parameters — also seeded into the controls panel. */
  currentParameters: Parameters;
  /** id of the most recent generated image (used for save-to-catalogue). */
  currentImageId: string | null;
  /** id of the image this iteration was based on (null on first run). */
  parentId: string | null;
  /**
   * Seed of the most recent generation. Backend echoes it back so the
   * next iteration uses the same seed (deterministic; only prompted
   * changes vary).
   */
  currentSeed: number | null;
  /**
   * v7 — plain-language description of the current image (aphantasia aid).
   * Populated lazily after each generation lands. Plain mode only — the UI
   * gates the rendering on `lang === 'plain'`.
   */
  currentDescription: string | null;
  /** 'idle' / 'sending' / 'error'. */
  status: ChatStatus;
  /** True while a chat request is in flight. */
  sending: boolean;
  /** Friendly error message from the last failed request (or null). */
  errorMessage: string | null;
  /** True when lastUserMessage can be retried. */
  canRetry: boolean;
  /** Last user-visible message — used by the retry button. */
  lastUserMessage: string | null;

  /**
   * When n_variations=4 was used, holds the four candidate items until
   * the user picks one (or dismisses).
   */
  variations: ChatResponseItem[] | null;
  /** True while variations are visible and the user has not picked one. */
  pickingVariation: boolean;

  /**
   * Send a free-form chat message. Aborts any in-flight request before
   * starting a new one.
   */
  send: (userMessage: string) => Promise<ChatResponse | null>;
  /** Apply the user's current slider settings with a synthesised message. */
  applyParameters: (
    parameters: Parameters,
    message?: string,
    /**
     * v7.1 — when `true`, the request is expected to produce a
     * variations grid; the hook will skip the initial `setVariations(null)`
     * so a rapid second click doesn't flash the grid empty.
     */
    options?: { expectVariations?: boolean },
  ) => Promise<ChatResponse | null>;
  /** Fresh generation using the supplied parameters. */
  generateFresh: (parameters: Parameters) => Promise<ChatResponse | null>;
  /** Commit one variation as the new canonical current image. */
  pickVariation: (item: ChatResponseItem) => void;
  /** Discard the current variations grid without picking one. */
  dismissVariations: () => void;
  /** Update a single top-level field on `currentParameters`. */
  updateParameter: <K extends keyof Parameters>(
    key: K,
    value: Parameters[K],
  ) => void;
  /** Replace a nested group on `currentParameters` (catchlight / etc.). */
  updateParameterGroup: <K extends keyof Parameters>(
    key: K,
    value: Parameters[K],
  ) => void;
  /** v6 — replace the entire Parameters object (used by the preset bar). */
  setParameters: (next: Parameters) => void;
  /** Save the current image to the catalogue. */
  saveToCatalogue: (
    input: Omit<SaveCatalogueRequest, 'generated_id'> & { generated_id?: string },
  ) => Promise<CatalogueItem | null>;
  /** True while a catalogue save is in flight. */
  savingToCatalogue: boolean;
  /** Friendly error from the last catalogue-save attempt. */
  saveErrorMessage: string | null;
  /** Reset the chat session back to "no image, empty history". */
  reset: () => void;
  /** Clear the last error message. */
  clearError: () => void;

  // ─── v5 additions ────────────────────────────────────────────────
  /** v5 — abort the active request and revert to idle. */
  cancel: () => void;
  /** v5 — re-send the most recent user-visible message. */
  retry: () => Promise<ChatResponse | null>;
  /**
   * v5 — apply a saved eye's parameters as the new starting point
   * without going through chat. Updates `currentImageUrl`,
   * `currentThumbnailUrl`, `currentImageId`, `currentParameters`,
   * `currentSeed`, `parentId` in one shot and appends an assistant
   * explanation entry.
   */
  setAsStartingPoint: (input: {
    imageId: string;
    imageUrl: string;
    thumbnailUrl: string;
    parameters: Parameters;
    seed: number;
    parentId?: string | null;
    note?: string;
  }) => void;

  /** v7.2 — per-eye annotation hotspots. Persisted across reloads. */
  annotations: Annotation[];
  /** v7.2 — replace the per-eye annotation list. */
  setAnnotations: (next: Annotation[]) => void;
  /** v7.2 — true after a successful hydrate from localStorage. */
  showRestoredNotice: boolean;
  /** v7.2 — dismiss the restored-session banner without clearing state. */
  dismissRestoredNotice: () => void;
  /** v7.2 — wipe the persisted session and reset all in-memory state. */
  onClearAll: () => void;
}

/**
 * Primary chat hook for the Studio.
 *
 * Auto-aborts any in-flight request when `send` / `applyParameters` /
 * `generateFresh` is called again — a stale response cannot overwrite a
 * fresh one because the request is cancelled before the `await` resolves.
 */
export function useChat(): UseChatResult {
  const [chatHistory, setChatHistory] = useState<ChatTurn[]>([]);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string | null>(
    null,
  );
  const [currentParameters, setCurrentParameters] =
    useState<Parameters>(DEFAULT_PARAMETERS);
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [currentSeed, setCurrentSeed] = useState<number | null>(null);
  const [status, setStatus] = useState<ChatStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null);
  const [savingToCatalogue, setSavingToCatalogue] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [variations, setVariations] = useState<ChatResponseItem[] | null>(null);
  // v7 — plain-language description for the current image (aphantasia aid).
  const [currentDescription, setCurrentDescription] = useState<string | null>(null);
  // v7.2 — per-eye annotation hotspots. Lives here (not in Studio) so the
  // localStorage hydrate/persist effects can read & write it in lock-step
  // with currentImageId.
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  // v7.2 — true after a successful hydrate from localStorage so the chat
  // panel can show the "Restored from your last session" banner.
  const [showRestoredNotice, setShowRestoredNotice] = useState(false);
  // v7 — keep a ref so pickVariation can snapshot the variations even though
  // it clears the state synchronously.
  const variationsRef = useRef<ChatResponseItem[] | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    variationsRef.current = variations;
  }, [variations]);

  /* ---------------------------------------------------------------- *
   * v7.2 — localStorage persistence                                 *
   * ---------------------------------------------------------------- */

  // Hydrate once on mount — restore the persisted eye + chat history
  // (if any) and surface a "Restored from your last session" banner.
  // The 24h TTL on the eye helper prevents stale images from being
  // resurrected long after their session ended.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = loadPersistedEye();
    if (!saved) return;
    setCurrentImageId(saved.id);
    setCurrentImageUrl(saved.image_url);
    setCurrentThumbnailUrl(saved.thumbnail_url);
    setCurrentParameters(saved.parameters);
    setCurrentSeed(saved.seed);
    setCurrentDescription(saved.description);
    setParentId(saved.parent_id);
    setAnnotations(loadAnnotations(saved.id));
    const persistedChat = loadPersistedChat();
    if (persistedChat && persistedChat.length > 0) {
      // wire ChatTurn (role, content, diff?) → internal ChatTurn (adds
      // a numeric id for stable React keys). Re-assigned by index;
      // monotonic ids only matter within a single session.
      let nextId = 1;
      setChatHistory(persistedChat.map((t) => ({
        id: nextId++, role: t.role, content: t.content, diff: t.diff,
      })));
      setShowRestoredNotice(true);
    }
  }, []);

  // Persist chat history with a 300 ms debounce so rapid bursts (typing,
  // streaming responses) collapse into one localStorage write. Maps
  // internal ChatTurn → wire ChatTurn so the persisted blob stays
  // portable (matches what /api/chat sends).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      savePersistedChat(chatHistory.map((t) => ({
        role: t.role, content: t.content, diff: t.diff,
      })));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [chatHistory]);

  // Persist annotations per current eye id. When currentImageId changes
  // (new generation, lineage step, hydrate) the previous eye's hotspots
  // stay in localStorage keyed by their old id; this effect just keeps
  // the active eye's row up to date.
  useEffect(() => {
    if (currentImageId) saveAnnotations(currentImageId, annotations);
  }, [annotations, currentImageId]);

  /** Always points at the most-recent in-flight AbortController. */
  const abortRef = useRef<AbortController | null>(null);
  /** Monotonic id assigned to each in-flight request so stale responses can be ignored. */
  const requestSeqRef = useRef(0);
  /** Monotonic id assigned to every chat turn (user + assistant). */
  const turnIdRef = useRef(0);

  /** Snapshot of parameters sent with the most recent request — used by retry + diff. */
  const lastRequestParamsRef = useRef<Parameters | null>(null);

  /**
   * Common send path used by `send`, `applyParameters`, and
   * `generateFresh`. The wire URL is the only thing that varies.
   */
  const sendInternal = useCallback(
    async (
      userMessage: string,
      parameters: Parameters,
      options: {
        path: '/chat' | '/generate';
        silentHistory?: boolean;
        forceVariations?: boolean;
        /**
         * v7.1 — when `true`, the request is expected to produce a
         * variations grid. The sendInternal body will not wipe the
         * existing `variations` state at the top, so a rapid second
         * click on "Generate 4 variations" no longer flashes the grid
         * empty while the new request is in flight.
         */
        expectVariations?: boolean;
      },
    ): Promise<ChatResponse | null> => {
      // Cancel any in-flight request before starting a new one.
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const mySeq = ++requestSeqRef.current;

      const trimmed = userMessage.trim();
      lastRequestParamsRef.current = parameters;

      if (!options.silentHistory && trimmed) {
        setLastUserMessage(trimmed);
        setChatHistory((prev) => [
          ...prev,
          { id: ++turnIdRef.current, role: 'user', content: trimmed },
        ]);
      }

      setStatus('sending');
      setErrorMessage(null);
      // v7.1 — variations race fix. When the new request is itself a
      // variations request (e.g. the user clicked "Generate 4 variations"
      // twice rapidly), do NOT clear the existing variations grid on the
      // way out — the still-in-flight first request may resolve to a
      // fresh grid before the user sees an empty flash. Only the
      // non-variation paths (single /chat, /generate) wipe it.
      if (!options.expectVariations) {
        setVariations(null);
      }
      // v7.2 — sending a new message dismisses the "Restored from your
      // last session" banner. Once mum is actively chatting again, the
      // session is no longer "restored" — it's live.
      setShowRestoredNotice(false);

      try {
        let resp: ChatResponse | ChatVariationResponse;
        if (options.path === '/chat') {
          const body = {
            current_parameters: parameters,
            current_image_url: currentImageUrl,
            current_seed: currentSeed,
            chat_history: buildHistoryForRequest(chatHistory),
            user_message: trimmed,
          };
          const r = await api.chat(body, ctrl.signal);
          resp = r;
        } else {
          const r = await api.generateFresh(
            { parameters },
            ctrl.signal,
          );
          resp = r;
        }

        // If a newer request started during our await, drop this result.
        if (mySeq !== requestSeqRef.current || ctrl.signal.aborted) {
          return null;
        }

        // Variation response → hold them in state for the user to pick.
        if (isVariationResponse(resp)) {
          if (resp.items.length === 0) {
            setStatus('idle');
            return null;
          }
          setVariations(resp.items);
          setStatus('idle');
          return null;
        }

        // Single-response path.
        const single = resp as ChatResponse;
        setCurrentImageUrl(single.image_url);
        setCurrentThumbnailUrl(single.thumbnail_url);
        setCurrentParameters(single.parameters);
        setCurrentImageId(single.id);
        setParentId(single.parent_id);
        setCurrentSeed(single.seed);
        // v7 — clear stale description so it doesn't show under the wrong eye.
        setCurrentDescription(null);
        // v7.2 — persist the new eye snapshot so a tab reload restores it.
        savePersistedEye({
          id: single.id,
          image_url: single.image_url,
          thumbnail_url: single.thumbnail_url,
          parameters: single.parameters,
          parent_id: single.parent_id,
          seed: single.seed,
          description: null,
          createdAt: Date.now(),
        });

        // v5 — compute the diff from before/after if the backend didn't
        // already include one. `parameters` here is what the user sent;
        // `single.parameters` is what the LLM produced.
        const computedDiff = computeParameterDiff(parameters, single.parameters);
        const diff =
          single.diff && single.diff.length > 0 ? single.diff : computedDiff;

        const explanation = single.explanation ?? 'Updated the eye based on your message.';
        // v7 — fetch a plain-language description in parallel. Fire-and-forget;
        // if the request fails the chat still works, just without the subtitle.
        // We use the LLM-produced parameters (single.parameters) so the
        // description matches the image that actually came back.
        const myTurnId = ++turnIdRef.current;
        if (!options.silentHistory) {
          setChatHistory((prev) => [
            ...prev,
            { id: myTurnId, role: 'assistant', content: explanation, diff },
          ]);
        } else if (options.path === '/chat') {
          if (trimmed) {
            setChatHistory((prev) => [
              ...prev,
              { id: myTurnId, role: 'assistant', content: explanation, diff },
            ]);
          }
        } else if (options.path === '/generate') {
          setChatHistory((prev) => [
            ...prev,
            { id: myTurnId, role: 'assistant', content: explanation, diff },
          ]);
        }
        // v7 — fetch description in the background and attach it to the turn.
        void api
          .describe(single.parameters)
          .then((description) => {
            setChatHistory((prev) =>
              prev.map((t) => (t.id === myTurnId ? { ...t, description } : t)),
            );
            // Also update the rolling `currentDescription` so the save panel
            // can render it next to the thumbnail.
            setCurrentDescription((prev) => (prev === null ? description : prev));
            // v7.2 — re-persist the eye now that description is known so
            // a tab reload restores both the image AND its description.
            savePersistedEye({
              id: single.id,
              image_url: single.image_url,
              thumbnail_url: single.thumbnail_url,
              parameters: single.parameters,
              parent_id: single.parent_id,
              seed: single.seed,
              description,
              createdAt: Date.now(),
            });
          })
          .catch(() => {
            /* swallow — description is decorative */
          });
        setStatus('idle');
        return single;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Cancelled — leave status at 'idle' so mum can keep typing.
          setStatus((s) => (s === 'sending' ? 'idle' : s));
          return null;
        }
        if (mySeq !== requestSeqRef.current) return null;

        const friendly = friendlyError(err);
        setErrorMessage(friendly.message);
        setStatus('error');
        return null;
      }
    },
    [chatHistory, currentImageUrl, currentSeed],
  );

  const send = useCallback(
    (userMessage: string) =>
      sendInternal(userMessage, currentParameters, {
        path: '/chat',
        silentHistory: false,
      }),
    [sendInternal, currentParameters],
  );

  const applyParameters = useCallback(
    (
      parameters: Parameters,
      message = 'Apply these parameter changes',
      options?: { expectVariations?: boolean },
    ) =>
      sendInternal(message, parameters, {
        path: '/chat',
        silentHistory: false,
        expectVariations: options?.expectVariations,
      }),
    [sendInternal],
  );

  const generateFresh = useCallback(
    (parameters: Parameters) =>
      sendInternal('Generate a fresh eye', parameters, {
        path: '/generate',
        silentHistory: false,
      }),
    [sendInternal],
  );

  const pickVariation = useCallback((item: ChatResponseItem) => {
    // Snapshot of the variations grid BEFORE we clear it, plus the chosen id.
    const snapshot = variationsRef.current ?? [];
    setCurrentImageUrl(item.image_url);
    setCurrentThumbnailUrl(item.thumbnail_url);
    setCurrentParameters(item.parameters);
    setCurrentImageId(item.id);
    setParentId(item.parent_id);
    setCurrentSeed(item.seed);
    setVariations(null);
    // v7.2 — persist the picked variation as the new "last eye".
    savePersistedEye({
      id: item.id,
      image_url: item.image_url,
      thumbnail_url: item.thumbnail_url,
      parameters: item.parameters,
      parent_id: item.parent_id,
      seed: item.seed,
      description: null,
      createdAt: Date.now(),
    });
    const myTurnId = ++turnIdRef.current;
    setChatHistory((prev) => [
      ...prev,
      {
        id: myTurnId,
        role: 'assistant',
        content: 'Picked one variation as the new reference.',
        variation: {
          items: snapshot.map((s) => ({
            id: s.id,
            image_url: s.image_url,
            thumbnail_url: s.thumbnail_url,
            seed: s.seed,
          })),
          selected_id: item.id,
        },
      },
    ]);
    // v7 — also fetch a description for the picked eye and attach to the turn.
    void api
      .describe(item.parameters)
      .then((description) => {
        setChatHistory((prev) =>
          prev.map((t) => (t.id === myTurnId ? { ...t, description } : t)),
        );
        setCurrentDescription((prev) => (prev === null ? description : prev));
        // v7.2 — re-persist with description now populated.
        savePersistedEye({
          id: item.id,
          image_url: item.image_url,
          thumbnail_url: item.thumbnail_url,
          parameters: item.parameters,
          parent_id: item.parent_id,
          seed: item.seed,
          description,
          createdAt: Date.now(),
        });
      })
      .catch(() => {
        /* swallow */
      });
  }, []);

  const dismissVariations = useCallback(() => {
    setVariations(null);
  }, []);

  const updateParameter = useCallback(
    <K extends keyof Parameters>(key: K, value: Parameters[K]) => {
      setCurrentParameters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const updateParameterGroup = useCallback(
    <K extends keyof Parameters>(key: K, value: Parameters[K]) => {
      setCurrentParameters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  /**
   * v6 — replace the entire Parameters object. Used by the preset bar so
   * applying a preset overwrites every field in one shot rather than
   * firing `updateParameter` for each one.
   */
  const setParameters = useCallback((next: Parameters) => {
    setCurrentParameters(next);
  }, []);

  const saveToCatalogue = useCallback(
    async (
      input: Omit<SaveCatalogueRequest, 'generated_id'> & {
        generated_id?: string;
      },
    ): Promise<CatalogueItem | null> => {
      const generatedId = input.generated_id ?? currentImageId;
      if (!generatedId) {
        setSaveErrorMessage('Generate a reference first, then save it.');
        return null;
      }
      setSavingToCatalogue(true);
      setSaveErrorMessage(null);
      try {
        const item = await api.saveToCatalogue({
          generated_id: generatedId,
          title: input.title,
          notes: input.notes,
          tags: input.tags,
          mood: input.mood,
          colour_family: input.colour_family,
          project_tag: input.project_tag,
          board_id: input.board_id,
          annotations: input.annotations,
        });
        return item;
      } catch (err) {
        setSaveErrorMessage(friendlyError(err).message);
        return null;
      } finally {
        setSavingToCatalogue(false);
      }
    },
    [currentImageId],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setChatHistory([]);
    setCurrentImageUrl(null);
    setCurrentThumbnailUrl(null);
    setCurrentParameters(DEFAULT_PARAMETERS);
    setCurrentImageId(null);
    setParentId(null);
    setCurrentSeed(null);
    setStatus('idle');
    setErrorMessage(null);
    setLastUserMessage(null);
    setSaveErrorMessage(null);
    setVariations(null);
    setCurrentDescription(null);
    setShowRestoredNotice(false);
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage(null);
    setSaveErrorMessage(null);
  }, []);

  // v5 — cancel the active request without resetting state.
  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStatus((s) => (s === 'sending' ? 'idle' : s));
    setErrorMessage(null);
  }, []);

  // v5 — re-send the most recent user-visible message. Useful for the
  // "Try again" button after a failure.
  const retry = useCallback(async (): Promise<ChatResponse | null> => {
    if (!lastUserMessage) return null;
    // Drop the trailing assistant turn (if any) and re-send.
    setChatHistory((prev) => {
      let i = prev.length - 1;
      while (i >= 0 && prev[i]?.role !== 'user') i--;
      // Remove everything from the user turn onward (the user re-sends,
      // so the assistant explanation is stale).
      return prev.slice(0, Math.max(0, i));
    });
    const params = lastRequestParamsRef.current ?? currentParameters;
    return sendInternal(lastUserMessage, params, {
      path: '/chat',
      silentHistory: true,
    });
  }, [currentParameters, lastUserMessage, sendInternal]);

  // v5 — adopt a saved eye as the new starting point.
  const setAsStartingPoint = useCallback(
    (input: {
      imageId: string;
      imageUrl: string;
      thumbnailUrl: string;
      parameters: Parameters;
      seed: number;
      parentId?: string | null;
      note?: string;
    }) => {
      setCurrentImageUrl(input.imageUrl);
      setCurrentThumbnailUrl(input.thumbnailUrl);
      setCurrentImageId(input.imageId);
      setCurrentParameters(input.parameters);
      setCurrentSeed(input.seed);
      setParentId(input.parentId ?? null);
      setVariations(null);
      setCurrentDescription(null);
      // v7.2 — adopt this eye as the persisted "last eye" so a reload
      // restores the chosen catalogue / lineage entry, not whatever was
      // there before. Annotations for the new id are loaded by the
      // `currentImageId`-watching useEffect below.
      savePersistedEye({
        id: input.imageId,
        image_url: input.imageUrl,
        thumbnail_url: input.thumbnailUrl,
        parameters: input.parameters,
        parent_id: input.parentId ?? null,
        seed: input.seed,
        description: null,
        createdAt: Date.now(),
      });
      const myTurnId = ++turnIdRef.current;
      setChatHistory((prev) => [
        ...prev,
        {
          id: myTurnId,
          role: 'assistant',
          content:
            input.note ??
            'Started from a saved eye. Type what to change next.',
        },
      ]);
      // v7 — also fetch description for the loaded eye.
      void api
        .describe(input.parameters)
        .then((description) => {
          setChatHistory((prev) =>
            prev.map((t) => (t.id === myTurnId ? { ...t, description } : t)),
          );
          setCurrentDescription((prev) => (prev === null ? description : prev));
          // v7.2 — re-persist with description populated.
          savePersistedEye({
            id: input.imageId,
            image_url: input.imageUrl,
            thumbnail_url: input.thumbnailUrl,
            parameters: input.parameters,
            parent_id: input.parentId ?? null,
            seed: input.seed,
            description,
            createdAt: Date.now(),
          });
        })
        .catch(() => {
          /* swallow */
        });
    },
    [],
  );

  /* v7.2 — when currentImageId changes (including the hydrate → first
   * load case), load that eye's persisted annotations into state.
   * The ref guard prevents re-loading on every render once an id has
   * been hydrated, while still reacting to genuine id changes.
   */
  const lastLoadedEyeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentImageId) {
      setAnnotations([]);
      lastLoadedEyeIdRef.current = null;
      return;
    }
    if (lastLoadedEyeIdRef.current === currentImageId) return;
    lastLoadedEyeIdRef.current = currentImageId;
    setAnnotations(loadAnnotations(currentImageId));
  }, [currentImageId]);

  const dismissRestoredNotice = useCallback(() => {
    setShowRestoredNotice(false);
  }, []);

  const onClearAll = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearPersistedEye();
    clearPersistedChat();
    clearAllAnnotations();
    setChatHistory([]);
    setCurrentImageUrl(null);
    setCurrentThumbnailUrl(null);
    setCurrentParameters(DEFAULT_PARAMETERS);
    setCurrentImageId(null);
    setParentId(null);
    setCurrentSeed(null);
    setStatus('idle');
    setErrorMessage(null);
    setLastUserMessage(null);
    setSaveErrorMessage(null);
    setVariations(null);
    setCurrentDescription(null);
    setAnnotations([]);
    setShowRestoredNotice(false);
    lastLoadedEyeIdRef.current = null;
  }, []);

  const sending = useMemo(() => status === 'sending', [status]);
  const pickingVariation = useMemo(
    () => variations !== null && variations.length > 0,
    [variations],
  );
  const canRetry = useMemo(
    () => status === 'error' && Boolean(lastUserMessage),
    [status, lastUserMessage],
  );

  return {
    chatHistory,
    currentImageUrl,
    currentThumbnailUrl,
    currentParameters,
    currentImageId,
    parentId,
    currentSeed,
    currentDescription,
    status,
    sending,
    errorMessage,
    canRetry,
    lastUserMessage,
    variations,
    pickingVariation,
    send,
    applyParameters,
    generateFresh,
    pickVariation,
    dismissVariations,
    updateParameter,
    updateParameterGroup,
    setParameters,
    saveToCatalogue,
    savingToCatalogue,
    saveErrorMessage,
    reset,
    clearError,
    cancel,
    retry,
    setAsStartingPoint,
    annotations,
    setAnnotations,
    showRestoredNotice,
    dismissRestoredNotice,
    onClearAll,
  };
}

/**
 * Build the chat_history payload sent on the wire.
 */
function buildHistoryForRequest(history: ChatTurn[]): ChatMessage[] {
  return history.map<ChatMessage>((t) => ({ role: t.role, content: t.content }));
}
